import classNames from "classnames";
import React, { type Ref, useImperativeHandle, useRef } from "react";
import "./Flex.css";

export type Gap = "xsmall" | "small" | "medium" | "medium-plus" | "large" | "xlarge";
export type AlignItems = "start" | "center" | "end" | "stretch";
export type AlignSelf = "start" | "center" | "end";
type JustifyContent =
    | "start"
    | "center"
    | "end"
    | "space-between"
    | "space-around"
    | "space-evenly";
type FlexWrap = "wrap" | "nowrap" | "wrap-reverse";

type FlexProps = {
    id?: string;
    className?: string;
    gap?: Gap;
    alignItems?: AlignItems;
    justifyContent?: JustifyContent;
    wrap?: FlexWrap;
    onClick?: (e: any) => void;
    children: React.ReactNode;
};

function gapCss(gap?: Gap) {
    return gap ? `flex-gap--${gap}` : undefined;
}

function alignCss(alignItems: AlignItems) {
    return `flex-align--${alignItems}`;
}

function alignSelfCss(alignSelf: AlignSelf) {
    return `flex-align-self--${alignSelf}`;
}

function justifyCss(justifyContent?: JustifyContent) {
    return justifyContent ? `flex-justify--${justifyContent}` : undefined;
}

function wrapCss(wrap?: FlexWrap) {
    return wrap ? `flex-wrap--${wrap}` : undefined;
}

const FlexRow = React.forwardRef(
    (props: FlexProps, ref: Ref<HTMLDivElement | null>) => {
        const {
            children,
            className,
            gap,
            alignItems = "center",
            justifyContent,
            wrap,
            id,
            onClick,
        } = props;

        const divRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => {
            if (!divRef.current) {
                throw new Error("FlexRow ref is null - component not properly mounted");
            }
            return divRef.current;
        });

        return (
            <div
                id={id}
                onClick={onClick}
                className={classNames(
                    "flex-row",
                    gapCss(gap),
                    alignCss(alignItems),
                    justifyCss(justifyContent),
                    wrapCss(wrap),
                    className,
                )}
                ref={divRef}
            >
                {children}
            </div>
        );
    },
);

const FlexColumn = React.forwardRef(
    (props: FlexProps, ref: Ref<HTMLDivElement | null>) => {
        const {
            children,
            className,
            gap,
            alignItems = "start",
            justifyContent,
            wrap,
            id,
            onClick,
        } = props;

        const divRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => {
            if (!divRef.current) {
                throw new Error(
                    "FlexColumn ref is null - component not properly mounted",
                );
            }
            return divRef.current;
        });

        return (
            <div
                id={id}
                onClick={onClick}
                className={classNames(
                    "flex-column",
                    gapCss(gap),
                    alignCss(alignItems),
                    justifyCss(justifyContent),
                    wrapCss(wrap),
                    className,
                )}
                ref={divRef}
            >
                {children}
            </div>
        );
    },
);

type InlineFlexProps = {
    className?: string;
    gap?: Gap;
    alignSelf?: AlignSelf;
    wrap?: FlexWrap;
    onClick?: (e: any) => void;
    children: React.ReactNode;
};

export function FlexInline(props: InlineFlexProps) {
    const { className, children, gap, alignSelf, wrap, onClick } = props;

    return (
        <span
            className={classNames(
                "flex-inline",
                className,
                gapCss(gap),
                alignCss("center"),
                alignSelfCss(alignSelf || "center"),
                wrapCss(wrap),
            )}
            onClick={onClick}
        >
            {children}
        </span>
    );
}

/**
 * @deprecated Use Lib.Flex instead
 */
export const Flex = {
    Row: FlexRow,
    Column: FlexColumn,
};
